import { UserRole } from '@/lib/enums';
import { NAV_ITEMS, type NavItemId } from '@/lib/navigation/nav-registry';

import { type NavigationCommand } from '../types';

/**
 * Builds a NavigationCommand by referencing a single source of truth in
 * `nav-registry`. Only `id`, `labelKey`, `keywords` and `groupKey` are
 * palette-specific — the rest (href, icon, roles) come from the registry to
 * prevent drift across surfaces (sidebar / bottom-nav / palette).
 */
function fromRegistry(
  registryId: NavItemId,
  command: Pick<NavigationCommand, 'id' | 'labelKey' | 'keywords' | 'groupKey'>,
): NavigationCommand {
  const def = NAV_ITEMS[registryId];
  return {
    id: command.id,
    labelKey: command.labelKey,
    href: def.href,
    icon: def.icon,
    roles: [...def.roles] as UserRole[],
    keywords: command.keywords,
    groupKey: command.groupKey,
  };
}

export const navigationCommands: NavigationCommand[] = [
  // Overview
  fromRegistry('home', {
    id: 'dashboard',
    labelKey: 'navigation.dashboard',
    keywords: ['home', 'início', 'overview'],
    groupKey: 'groups.overview',
  }),

  // Daily Operations
  fromRegistry('menuSelection', {
    id: 'menu-selection',
    labelKey: 'navigation.menuSelection',
    keywords: ['meals', 'refeições', 'escolher', 'select'],
    groupKey: 'groups.dailyOperations',
  }),
  fromRegistry('menuPlanner', {
    id: 'weekly-planner',
    labelKey: 'navigation.weeklyPlanner',
    keywords: ['menu', 'week', 'semana', 'planeamento'],
    groupKey: 'groups.dailyOperations',
  }),
  fromRegistry('dishes', {
    id: 'dishes',
    labelKey: 'navigation.dishes',
    keywords: ['pratos', 'meals', 'food', 'comida'],
    groupKey: 'groups.dailyOperations',
  }),
  fromRegistry('dishCategories', {
    id: 'dish-categories',
    labelKey: 'navigation.dishCategories',
    keywords: ['categorias', 'tags', 'tipos'],
    groupKey: 'groups.dailyOperations',
  }),
  fromRegistry('distribution', {
    id: 'distribution',
    labelKey: 'navigation.distribution',
    keywords: ['delivery', 'entrega', 'scan', 'qr'],
    groupKey: 'groups.dailyOperations',
  }),

  // Administration
  fromRegistry('employeeManagement', {
    id: 'employee-management',
    labelKey: 'navigation.employeeManagement',
    keywords: [
      'users',
      'user',
      'utilizadores',
      'staff',
      'funcionários',
      'employees',
      'workers',
      'pessoas',
    ],
    groupKey: 'groups.administration',
  }),
  fromRegistry('employeeManagementCreate', {
    id: 'create-employee',
    labelKey: 'navigation.createEmployee',
    keywords: ['add', 'novo', 'new', 'adicionar'],
    groupKey: 'groups.administration',
  }),
  fromRegistry('employeeManagementBulk', {
    id: 'bulk-create',
    labelKey: 'navigation.bulkCreate',
    keywords: ['import', 'csv', 'multiple', 'vários'],
    groupKey: 'groups.administration',
  }),
  fromRegistry('employeeOrders', {
    id: 'employee-orders',
    labelKey: 'navigation.employeeOrders',
    keywords: ['pedidos', 'encomendas'],
    groupKey: 'groups.administration',
  }),
  fromRegistry('companies', {
    id: 'companies',
    labelKey: 'navigation.companyManagement',
    keywords: ['empresas', 'organizations', 'organizações'],
    groupKey: 'groups.administration',
  }),
  fromRegistry('canteens', {
    id: 'canteens',
    labelKey: 'navigation.canteenManagement',
    keywords: ['cantinas', 'restaurants', 'restaurantes'],
    groupKey: 'groups.administration',
  }),

  // Support
  fromRegistry('reportIssues', {
    id: 'report-issues',
    labelKey: 'navigation.reportIssues',
    keywords: ['bug', 'problema', 'help', 'ajuda', 'support'],
    groupKey: 'groups.support',
  }),
  fromRegistry('profile', {
    id: 'profile',
    labelKey: 'navigation.profileSettings',
    keywords: ['account', 'conta', 'preferences', 'preferências'],
    groupKey: 'groups.support',
  }),
];

export function getNavigationCommandsForRoles(userRoles: UserRole[]): NavigationCommand[] {
  return navigationCommands.filter((command) =>
    command.roles.some((role) => userRoles.includes(role)),
  );
}

export function groupNavigationCommands(
  commands: NavigationCommand[],
): Record<string, NavigationCommand[]> {
  return commands.reduce(
    (acc, command) => {
      if (!acc[command.groupKey]) {
        acc[command.groupKey] = [];
      }
      acc[command.groupKey].push(command);
      return acc;
    },
    {} as Record<string, NavigationCommand[]>,
  );
}
